// packages/report-components/src/SectionHeader.tsx
//
// ADR-0018 Fase 6 — chapter-style section header: ghosted sequence
// number on the left, H2 title + optional italic conclusion line on
// the right, hairline divider below.
//
// Numbering is a PROP here, not a context read. It used to be the 1-based
// index of the owning section's id in the sandbox's live outline
// (`useSectionId` + `useOutlinePosition`), which is a fact about the reading
// room: an outline exists, it is ordered, and this header sits inside a
// RegistrySection. On the report editor's canvas none of that holds — every
// block is its own section, so "a heading after two KPIs" would number
// itself 03. So the surface decides:
//
//   sequence={n}     draw the folio n (padded: 01, 02, …)
//   sequence={null}  draw no folio — the surface looked and found none
//   (omitted)        draw no folio
//
// The sandbox keeps a wrapper of the same name that reads the outline and
// passes the number down, so authored JSX and every golden are unchanged;
// an explicit `sequence` from the author wins over the context (plan
// 2026-09-09, Besluit 3).
//
// Typography unit — the title is keyed to `cqi` (the `.verity-sheet`
// container's inline size: 896px content box at full width, so
// 1cqi = 8.96px), not to `vw`. The sheet is the page; the iframe viewport
// is not, and it resizes whenever the iterate panel opens. THE DEGRADATION
// IS RELIED ON: with no ancestor container — email, and print, where
// shell.css sets `container-type: normal` on the sheet — `cqi` falls back
// to the small viewport unit, which is the viewport-keyed behaviour those
// surfaces have today.

// Sequence numeral (2026-08-13): the ghosted number is a FOLIO. Its size,
// colour and position now live in components.css under
// `.verity-section-header__sequence`, not here, for one reason: on a wide
// sheet it leaves the flow entirely and hangs in the sheet's left margin
// (`@container sheet (min-width: 896px)`), and a container query cannot
// reach an inline style. Everything the two positions share (family,
// leading, tracking, tabular figures) stays inline; everything that differs
// between them is in the stylesheet. The conclusion line's old
// `marginLeft: calc(clamp(...) + 16px)` compensation is gone with it — it
// re-derived the numeral's width a second time, and with the numeral out of
// flow on a wide sheet there is nothing left to compensate for. On a narrow
// sheet (and in email/print) the numeral stays in flow and the conclusion
// simply flows underneath it.

import * as React from "react";

export type SectionHeaderProps = {
  /** Section title — renders as the h2. Required. */
  title: string;
  /** ADR-0018 §E — italic "conclusion first" line above the data:
   * "Budget efficiency is driven by retargeting, not prospecting."
   * Reads like a McKinsey slide title. Optional. */
  conclusion?: string;
  /** 1-based folio number, drawn ghosted ahead of the title. `null` or
   * omitted → no numeral (never a "?" / "--" placeholder). Supplied by the
   * surface: the sandbox wrapper reads it off the live outline, the report
   * adapter counts headings in reading order. */
  sequence?: number | null;
};

function formatSequence(n: number): string {
  // Two-digit padded, editorial ordering: 01, 02, …, 99, then 100+.
  return n < 10 ? `0${n}` : `${n}`;
}

export function SectionHeader({
  title,
  conclusion,
  sequence: sequenceProp,
}: SectionHeaderProps): React.ReactElement {
  const sequence =
    typeof sequenceProp === "number" ? formatSequence(sequenceProp) : null;
  return (
    <header
      className="verity-section-header"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        // 0, not 32: the rhythm between sections is structural now —
        // `.verity-section + .verity-section { margin-top: 64px }` in
        // components.css owns it. A header that carries its own top margin
        // makes the gap depend on whether a section happens to start with
        // one.
        marginTop: 0,
        marginBottom: "var(--verity-space-md, 16px)",
        paddingBottom: "var(--verity-space-md, 16px)",
        borderBottom:
          "1px solid var(--verity-color-border-subtle, rgba(0,0,0,0.06))",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: "var(--verity-space-md, 16px)",
          minWidth: 0,
        }}
      >
        {sequence ? (
          <span
            aria-hidden="true"
            className="verity-section-header__sequence"
            style={{
              fontFamily:
                "var(--verity-font-display, ui-serif, Georgia, serif)",
              // font-size and colour: components.css. The size is smaller
              // on the sheet than in email/print, and only a stylesheet can
              // be container-gated.
              lineHeight: 0.9,
              fontWeight: 400,
              letterSpacing: "-0.04em",
              fontVariantNumeric: "tabular-nums",
              // The numeral sits on the header's line, ahead of the title.
              // Without this a long title squeezes it.
              flexShrink: 0,
            }}
          >
            {sequence}
          </span>
        ) : null}
        <h2
          style={{
            margin: 0,
            fontFamily:
              "var(--verity-font-display, ui-serif, Georgia, serif)",
            // 2.45cqi tops out at the 22px ceiling just under the sheet's
            // full 896px content width (1cqi = 8.96px) — the same
            // construction as before, re-based after the exec-summary
            // thesis came down to clamp(24px, 3.4cqi, 32px) on 2026-08-31.
            //
            // Re-basing was NOT optional. At 3.6cqi this title resolved to
            // the full 32px while the new thesis resolves to 30.5px, so
            // every chapter title rendered LARGER than the page headline it
            // sits under — the three display sizes on this sheet (header
            // lede 28px, thesis 30.5px, this) had collapsed into a 4px
            // band. The old thesis:title ratio was 46:32 ≈ 1.44; 30.5:21.9
            // ≈ 1.39 keeps that step, so the hierarchy reads as it was
            // designed to.
            fontSize: "clamp(18px, 2.45cqi, 22px)",
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
            fontWeight: 600,
            color: "var(--verity-color-ink, var(--verity-color-text, #111827))",
            flex: "1 1 auto",
            minWidth: 0,
          }}
        >
          {title}
        </h2>
      </div>
      {conclusion ? (
        <p
          style={{
            margin: 0,
            fontFamily:
              "var(--verity-font-display, ui-serif, Georgia, serif)",
            fontSize: "var(--verity-font-size-md, 15px)",
            lineHeight: 1.45,
            fontStyle: "italic",
            color: "var(--verity-color-text, #111827)",
            maxWidth: "var(--verity-measure-lede, 62ch)",
          }}
        >
          {conclusion}
        </p>
      ) : null}
    </header>
  );
}
